import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { adminAPI, paymentAPI, platformAPI } from '../services/api';
import '../styles/Dashboard.css';

const formatDelta = (delta) => {
  const value = Number(delta) || 0;
  return value > 0 ? `+${value}` : `${value}`;
};

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatCategoryLabel = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '—';
  if (normalized === 'referral_bonus') return 'bonus';
  return normalized;
};

const formatDetailsLabel = (entry) => formatLedgerEntryLabel(entry);

const formatDepositStatus = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'partial_success') return 'Partial Deposit';
  if (normalized === 'success') return 'Success';
  if (normalized === 'failed') return 'Failed';
  if (normalized === 'pending') return 'Pending';
  return status || 'â€”';
};

const formatMoney = (value, currency = 'USD') => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 'â€”';
  const amountText = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${amountText} ${currency}` : amountText;
};

const getAmountColor = (delta) => {
  const value = Number(delta) || 0;
  if (value > 0) return '#10b981';
  if (value < 0) return '#ef4444';
  return '#6b7280';
};

const toDisplayGameName = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'whot') return 'WHOT';
  if (normalized === 'reaction') return 'Reaction';
  if (normalized === 'lucky') return 'Lucky';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatWagerAmount = (entry) => {
  const perGame = Number(entry?.challenge_wager ?? entry?.meta?.wager ?? 0);
  const numGames = Math.max(1, Number(entry?.challenge_num_games ?? entry?.meta?.numGames ?? 1) || 1);
  if (!Number.isFinite(perGame) || perGame <= 0) return '';
  return numGames > 1 ? `${perGame} x ${numGames}` : `${perGame}`;
};

const getTransactionIcon = (entry) => {
  const category = String(entry?.category || '').trim().toLowerCase();
  const game = String(entry?.resolved_game || entry?.game || '').trim().toLowerCase();

  if (category === 'deposit') return '💰';
  if (category === 'withdrawal') return '💸';
  if (category === 'wager') {
    if (game === 'whot') return '🃏';
    if (game === 'chess') return '♟️';
    if (game === 'checkers') return '⬛';
    if (game === 'draughts') return '🏁';
    if (game === 'flappy') return '🐦';
    if (game === 'reaction') return '🎯';
    if (game === 'dice') return '🎲';
    return '🎮';
  }
  if (category === 'payout') return '🏆';
  if (category === 'refund') return '↩️';
  if (category === 'bonus') return '🎁';
  return '🧾';
};

const formatLedgerEntryLabel = (entry) => {
  const rawValue = String(entry?.description || '').trim();
  const normalized = rawValue.toLowerCase();
  if (!normalized) return '—';
  if (normalized.includes('referral_first_deposit_bonus') || (normalized.includes('referral') && normalized.includes('bonus'))) {
    return 'Referral bonus';
  }

  const gameName = toDisplayGameName(entry?.resolved_game || entry?.game) || 'Game';
  const wagerAmount = formatWagerAmount(entry);
  const labels = {
    challenge_wager_accept_creator: 'Wager locked for your challenge',
    challenge_wager_accept_opponent: 'Wager locked to accept challenge',
    challenge_wager_accept_opponent_ai: 'Wager locked to accept AI challenge',
    challenge_series_payout: 'Challenge winnings paid',
    challenge_series_payout_winner: 'Challenge winnings paid',
    challenge_series_payout_runner_up: 'Challenge runner-up payout',
    challenge_series_tie_refund_creator: 'Challenge tie refund',
    challenge_series_tie_refund_opponent: 'Challenge tie refund',
    challenge_forfeit_payout: 'Challenge forfeit payout',
    challenge_forfeit_mark_lost: 'Challenge forfeit recorded',
    challenge_stale_refund: 'Room closed refund',
    challenge_stale_escrow_refund_reconcile: 'Room closed refund',
    dice_challenge_payout: 'Dice challenge winnings paid',
    dice_challenge_payout_auto: 'Dice challenge winnings paid',
    dice_challenge_tie_refund: 'Dice challenge tie refund',
    dice_challenge_tie_refund_auto: 'Dice challenge tie refund',
    whot_challenge_payout: 'WHOT challenge winnings paid',
    multiplayer_wager_create: 'Seat wager locked when room was created',
    multiplayer_wager_join: 'Seat wager locked when you joined room',
    multiplayer_wager_replay: 'Replay wager locked',
    multiplayer_dice_payout: 'Multiplayer dice winnings paid',
    multiplayer_whot_payout: 'WHOT multiplayer winnings paid',
    multiplayer_cancel_refund: 'Room cancelled refund',
    whot_multiplayer_cancel_refund: 'WHOT room cancelled refund',
    matchmaking_wager_creator: 'Matchmaking wager locked when room was created',
    matchmaking_wager_opponent: 'Matchmaking wager locked when match was joined',
    dice_game_payout: 'Dice game winnings paid',
    dice_game_tie_refund_player1: 'Dice game tie refund',
    dice_game_tie_refund_player2: 'Dice game tie refund',
    flappy_match_escrow_create: 'Flappy wager locked when match was created',
    flappy_match_escrow_accept: 'Flappy wager locked when match was accepted',
    flappy_match_escrow_join: 'Flappy wager locked when match was joined',
    flappy_match_payout: 'Flappy winnings paid',
    flappy_match_tie_refund_creator: 'Flappy tie refund',
    flappy_match_tie_refund_opponent: 'Flappy tie refund',
    flappy_match_cancel_refund_creator: 'Flappy cancelled match refund',
    flappy_match_cancel_refund_opponent: 'Flappy cancelled match refund',
    flappy_match_stale_refund: 'Room closed refund',
    chess_match_escrow_create: 'Chess wager locked when match was created',
    chess_match_escrow_join: 'Chess wager locked when match was joined',
    chess_match_escrow_accept: 'Chess wager locked when match was accepted',
    chess_match_settle_win: 'Chess winnings paid',
    chess_match_refund_cancel_creator: 'Chess cancelled match refund',
    chess_match_refund_cancel_opponent: 'Chess cancelled match refund',
    chess_match_stale_refund: 'Room closed refund',
    chess_match_stale_escrow_refund_reconcile: 'Room closed refund',
    checkers_match_escrow_create: 'Checkers wager locked when match was created',
    checkers_match_escrow_join: 'Checkers wager locked when match was joined',
    checkers_match_escrow_accept: 'Checkers wager locked when match was accepted',
    checkers_match_settle_win: 'Checkers winnings paid',
    checkers_match_refund_cancel_creator: 'Checkers cancelled match refund',
    checkers_match_refund_cancel_opponent: 'Checkers cancelled match refund',
    checkers_match_stale_refund: 'Room closed refund',
    draughts_match_escrow_create: 'Draughts wager locked when match was created',
    draughts_match_escrow_join: 'Draughts wager locked when match was joined',
    draughts_match_escrow_accept: 'Draughts wager locked when match was accepted',
    draughts_match_settle_win: 'Draughts winnings paid',
    draughts_match_refund_cancel_creator: 'Draughts cancelled match refund',
    draughts_match_refund_cancel_opponent: 'Draughts cancelled match refund',
    draughts_match_stale_refund: 'Room closed refund',
    reaction_match_escrow_create: 'Reaction wager locked when match was created',
    reaction_match_escrow_accept: 'Reaction wager locked when match was accepted',
    reaction_match_escrow_join: 'Reaction wager locked when match was joined',
    reaction_match_settle_win: 'Reaction winnings paid',
    reaction_match_settle_tie_creator: 'Reaction tie refund',
    reaction_match_settle_tie_opponent: 'Reaction tie refund',
    reaction_match_refund_cancel_creator: 'Reaction cancelled match refund',
    reaction_match_stale_refund: 'Room closed refund',
    withdrawal_request_hold: 'Withdrawal request placed',
    withdrawal_admin_status_refund: 'Withdrawal refund',
    withdrawal_payout_failed_refund: 'Withdrawal failed refund',
    withdrawal_rejected_refund: 'Withdrawal rejected refund',
    nowpayments_deposit: 'Deposit credited',
    paystack_deposit: 'Deposit credited',
    admin_manual_deposit: 'Manual deposit credited',
  };

  let label = labels[normalized];
  if (!label) {
    return rawValue.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  if (normalized.includes('wager') && wagerAmount) {
    label += ` for ${gameName} (${wagerAmount})`;
  } else if (normalized.includes('payout') || normalized.includes('refund') || normalized.includes('forfeit')) {
    label += ` for ${gameName}`;
  }

  if (entry?.reference_type === 'challenge' && entry?.reference_id) {
    label += ` · Challenge #${entry.reference_id}`;
  }

  return label;
};

const Ledger = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [depositAmount, setDepositAmount] = useState('2');
  const [minimumDeposit, setMinimumDeposit] = useState(2);
  const [minimumWithdrawal, setMinimumWithdrawal] = useState(200);
  const [depositProvider, setDepositProvider] = useState('nowpayments');
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositMessage, setDepositMessage] = useState('');
  const [pendingReference, setPendingReference] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('200');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawNetwork, setWithdrawNetwork] = useState('bsc');
  const [withdrawProvider, setWithdrawProvider] = useState('nowpayments');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawMessage, setWithdrawMessage] = useState('');
  const [withdrawBankName, setWithdrawBankName] = useState('');
  const [withdrawAccountNumber, setWithdrawAccountNumber] = useState('');
  const [withdrawAccountName, setWithdrawAccountName] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [depositStatusFilter, setDepositStatusFilter] = useState('');
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [depositReceiptLoading, setDepositReceiptLoading] = useState(false);
  const [withdrawalMode, setWithdrawalMode] = useState('admin_approve');
  const [modeLoading, setModeLoading] = useState(false);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingActionId, setPendingActionId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [gameFilter, setGameFilter] = useState('');
  const [activeTab, setActiveTab] = useState('deposit');
  const [transactionsPage, setTransactionsPage] = useState(1);

  // Bank Transfer (Naira) state
  const [bankDepositAmount, setBankDepositAmount] = useState('1500');
  const [bankDepositDescription, setBankDepositDescription] = useState('');
  const [bankSettings, setBankSettings] = useState(null);
  const [bankTransferRequest, setBankTransferRequest] = useState(null);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);
  const [bankTransferMessage, setBankTransferMessage] = useState('');
  const [myBankTransfers, setMyBankTransfers] = useState([]);
  const [myBankTransfersLoading, setMyBankTransfersLoading] = useState(false);
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const TRANSACTIONS_PER_PAGE = 20;

  const providerOptions = useMemo(() => ([
    { value: 'nowpayments', label: 'NOWPayments', icon: '💰', description: 'USD/USDT crypto' },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦', description: '₦ Naira · ~20 min' },
  ]), []);

  const depositCurrencyLabel = 'USD';

  const selectedProviderLabel = useMemo(() => {
    const found = providerOptions.find((item) => item.value === depositProvider);
    return found?.label || 'Payment Gateway';
  }, [depositProvider, providerOptions]);

  const parsedDepositAmount = Math.max(0, Math.trunc(Number(depositAmount) || 0));
  const estimatedDepositCredit = Math.max(0, parsedDepositAmount * 100);

  const filteredEntries = useMemo(() => entries, [entries]);
  const totalTransactionPages = Math.max(1, Math.ceil(filteredEntries.length / TRANSACTIONS_PER_PAGE));
  const safeTransactionsPage = Math.min(Math.max(1, transactionsPage), totalTransactionPages);
  const paginatedEntries = useMemo(() => {
    const start = (safeTransactionsPage - 1) * TRANSACTIONS_PER_PAGE;
    return filteredEntries.slice(start, start + TRANSACTIONS_PER_PAGE);
  }, [filteredEntries, safeTransactionsPage]);

  useEffect(() => {
    setTransactionsPage(1);
  }, [categoryFilter, gameFilter]);

  useEffect(() => {
    if (transactionsPage > totalTransactionPages) {
      setTransactionsPage(totalTransactionPages);
    }
  }, [transactionsPage, totalTransactionPages]);

  const fetchLedger = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!user?.id) return;

    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await api.get('/ledger', {
        params: {
          limit: 500,
          category: categoryFilter || undefined,
          game: gameFilter || undefined
        }
      });
      const list = Array.isArray(res.data?.entries) ? res.data.entries : [];
      setEntries(list);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load transactions');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [user?.id, categoryFilter, gameFilter]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const fetchMyWithdrawals = useCallback(async () => {
    if (!user?.id) return;
    setWithdrawalsLoading(true);
    try {
      const res = await paymentAPI.listMyWithdrawals({ limit: 50 });
      const rows = Array.isArray(res?.data?.withdrawals) ? res.data.withdrawals : [];
      setWithdrawals(rows);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to load withdrawals');
    } finally {
      setWithdrawalsLoading(false);
    }
  }, [user?.id]);

  const fetchAdminWithdrawalData = useCallback(async () => {
    if (!isAdmin) return;
    setPendingLoading(true);
    try {
      const [modeRes, pendingRes] = await Promise.all([
        adminAPI.getWithdrawalMode(),
        paymentAPI.listPendingWithdrawals({ limit: 100 })
      ]);
      setWithdrawalMode(modeRes?.data?.withdrawalMode || 'admin_approve');
      const rows = Array.isArray(pendingRes?.data?.withdrawals) ? pendingRes.data.withdrawals : [];
      setPendingWithdrawals(rows);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to load admin withdrawal queue');
    } finally {
      setPendingLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchMyWithdrawals();
  }, [fetchMyWithdrawals]);

  const fetchMyDeposits = useCallback(async () => {
    if (!user?.id) return;
    setDepositsLoading(true);
    try {
      const res = await paymentAPI.listMyDeposits({ limit: 50, status: depositStatusFilter });
      const rows = Array.isArray(res?.data?.deposits) ? res.data.deposits : [];
      setDeposits(rows);
    } catch (_e) {
      // silent — deposit history is supplemental
    } finally {
      setDepositsLoading(false);
    }
  }, [user?.id, depositStatusFilter]);

  useEffect(() => {
    fetchMyDeposits();
  }, [fetchMyDeposits]);

  useEffect(() => {
    fetchAdminWithdrawalData();
  }, [fetchAdminWithdrawalData]);

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMinimum = Number(res?.data?.minimumDeposit);
        if (Number.isFinite(nextMinimum) && nextMinimum >= 0) {
          setMinimumDeposit(Math.trunc(nextMinimum));
          setDepositAmount((prev) => {
            const current = Math.trunc(Number(prev) || 0);
            if (current <= 0) return String(Math.trunc(nextMinimum));
            return String(Math.max(current, Math.trunc(nextMinimum)));
          });
        }
        const nextMinimumWithdrawal = Number(res?.data?.minimumWithdrawal);
        if (Number.isFinite(nextMinimumWithdrawal) && nextMinimumWithdrawal >= 0) {
          setMinimumWithdrawal(Math.trunc(nextMinimumWithdrawal));
          setWithdrawAmount((prev) => {
            const current = Math.trunc(Number(prev) || 0);
            if (current <= 0) return String(Math.trunc(nextMinimumWithdrawal));
            return String(Math.max(current, Math.trunc(nextMinimumWithdrawal)));
          });
        }
      } catch (e) {
        // Keep fallback default if platform settings are unavailable.
      }
    };

    loadPlatformSettings();
  }, []);

  // Fetch bank settings for bank transfer tab
  useEffect(() => {
    if (depositProvider !== 'bank_transfer') return;
    paymentAPI.getPublicBankSettings()
      .then((res) => setBankSettings(res?.data || null))
      .catch(() => {});
    // Also load previous requests
    setMyBankTransfersLoading(true);
    paymentAPI.listMyBankTransfers()
      .then((res) => setMyBankTransfers(Array.isArray(res?.data?.requests) ? res.data.requests : []))
      .catch(() => {})
      .finally(() => setMyBankTransfersLoading(false));
  }, [depositProvider]);

  const checkDepositStatus = useCallback(async (reference, opts = {}) => {
    const { showLoading = true } = opts;
    if (!reference) return null;

    if (showLoading) setDepositLoading(true);
    try {
      const res = await paymentAPI.getDepositStatus(reference);
      const status = res?.data?.deposit?.status;
      const providerError = res?.data?.providerVerificationError;
      const requestedAmount = Number(res?.data?.deposit?.amount || 0);
      const creditedAmount = Number(res?.data?.deposit?.creditedAmount || requestedAmount || 0);
      const receivedAmount = Number(res?.data?.deposit?.receivedAmount || 0);
      const minimumRequiredAmount = Number(res?.data?.deposit?.minimumRequiredAmount || 0);
      const belowMinimum = Boolean(res?.data?.deposit?.belowMinimum);
      const amountMismatch = Boolean(res?.data?.deposit?.amountMismatch);
      const isSuccess = status === 'success';
      const isPartialSuccess = status === 'partial_success';

      if (isSuccess) {
        if (amountMismatch || (creditedAmount > 0 && requestedAmount > 0 && creditedAmount !== requestedAmount)) {
          setDepositMessage(`Partial deposit credited. Requested: ${requestedAmount}, received: ${creditedAmount}. Wallet updated with the exact received amount.`);
        } else {
          setDepositMessage('Deposit successful. Your wallet has been updated.');
        }
        setPendingReference('');
        await refreshUser?.();
        await fetchLedger({ silent: true });
        await fetchMyDeposits();
        const url = new URL(window.location.href);
        url.searchParams.delete('reference');
        url.searchParams.delete('trxref');
        url.searchParams.delete('paymentReference');
        window.history.replaceState({}, '', `${url.pathname}${url.search}`);
      } else if (isPartialSuccess) {
        setDepositMessage(`Partial deposit credited. Received: ${creditedAmount}. Waiting for remaining payment or final provider confirmation.`);
        setPendingReference(reference);
        await refreshUser?.();
        await fetchLedger({ silent: true });
        await fetchMyDeposits();
      } else if (status === 'failed') {
        setDepositMessage('Deposit failed or was cancelled.');
        setPendingReference('');
      } else {
        if (belowMinimum && minimumRequiredAmount > 0) {
          setDepositMessage(`Payment received (${receivedAmount}), awaiting provider confirmation before crediting your wallet.`);
        } else {
          setDepositMessage(providerError ? `Pending confirmation (${providerError})` : 'Deposit is still pending confirmation.');
        }
        setPendingReference(reference);
      }

      return status;
    } catch (e) {
      setDepositMessage(e.response?.data?.error || 'Unable to verify deposit yet.');
      setPendingReference(reference);
      return null;
    } finally {
      if (showLoading) setDepositLoading(false);
    }
  }, [fetchLedger, fetchMyDeposits, refreshUser]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get('reference') || params.get('trxref') || params.get('paymentReference');
    if (!reference || !user?.id) return;
    setDepositMessage('Confirming deposit...');
    checkDepositStatus(reference, { showLoading: true });
  }, [user?.id, checkDepositStatus]);

  useEffect(() => {
    if (!pendingReference) return undefined;

    let active = true;
    let count = 0;
    const maxAttempts = 30;
    const timer = window.setInterval(async () => {
      if (!active) return;
      count += 1;
      const status = await checkDepositStatus(pendingReference, { showLoading: false });
      if (!active) return;
      if (status === 'success' || status === 'failed' || count >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 3500);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [pendingReference, checkDepositStatus]);

  const handleViewDeposit = useCallback(async (row) => {
    if (!row?.reference) return;

    setDepositReceiptLoading(true);
    try {
      const res = await paymentAPI.getDepositStatus(row.reference);
      setSelectedDeposit(res?.data?.deposit || row);
    } catch (_e) {
      setSelectedDeposit(row);
    } finally {
      setDepositReceiptLoading(false);
    }
  }, []);

  const handleStartDeposit = async () => {
    const amount = Math.trunc(Number(depositAmount) || 0);
    if (amount <= 0) {
      setDepositMessage('Please enter a valid deposit amount.');
      return;
    }
    if (amount < minimumDeposit) {
      setDepositMessage(`Minimum deposit is ${minimumDeposit} ${depositCurrencyLabel}.`);
      return;
    }

    setDepositLoading(true);
    setDepositMessage(`Initializing ${selectedProviderLabel} checkout...`);
    try {
      const res = await paymentAPI.initializeDeposit(amount, depositProvider);
      const url = res?.data?.authorizationUrl;
      if (!url) {
        throw new Error('Missing checkout URL from server');
      }
      window.location.href = url;
    } catch (e) {
      setDepositMessage(e.response?.data?.error || e.message || 'Failed to start deposit');
      setDepositLoading(false);
    }
  };

  const handleBankTransferRequest = async () => {
    const amtNaira = Math.round(Number(bankDepositAmount) * 100) / 100;
    if (!amtNaira || amtNaira < 100) {
      setBankTransferMessage('Minimum bank transfer amount is ₦100.');
      return;
    }
    const description = String(bankDepositDescription || '').trim();
    if (!description || description.length < 3) {
      setBankTransferMessage('Please enter a description (e.g. Sender Name, bank, or narration) of at least 3 characters.');
      return;
    }
    setBankTransferLoading(true);
    setBankTransferMessage('');
    setBankTransferRequest(null);
    try {
      const res = await paymentAPI.requestBankTransfer({ amountNaira: amtNaira, description });
      setBankTransferRequest(res?.data?.request || null);
      if (res?.data?.bankDetails) setBankSettings((prev) => ({ ...prev, ...res.data.bankDetails, accountNumber: res.data.bankDetails.accountNumber, bankTransferEnabled: true }));
      setBankTransferMessage('');
      setBankDepositDescription(''); // reset input on success
      // Refresh list
      paymentAPI.listMyBankTransfers()
        .then((r) => setMyBankTransfers(Array.isArray(r?.data?.requests) ? r.data.requests : []))
        .catch(() => {});
    } catch (e) {
      setBankTransferMessage(e.response?.data?.error || e.message || 'Failed to create transfer request');
    } finally {
      setBankTransferLoading(false);
    }
  };

  const [markingPaid, setMarkingPaid] = useState(false);

  const handleConfirmPaymentMade = async (requestId, refCode) => {
    if (!requestId) return;
    const confirm = window.confirm(`Have you completed the bank transfer? Only mark as paid if you have successfully transferred the money for reference ${refCode}.`);
    if (!confirm) return;

    setMarkingPaid(true);
    try {
      await paymentAPI.confirmBankTransferPaid(requestId);
      window.alert('Payment Marked Paid 🚀\n\nWe will verify your transfer and credit your wallet shortly.');
      
      if (bankTransferRequest && bankTransferRequest.id === requestId) {
        setBankTransferRequest((prev) => prev ? { ...prev, status: 'paid' } : null);
      }
      
      paymentAPI.listMyBankTransfers()
        .then((r) => setMyBankTransfers(Array.isArray(r?.data?.requests) ? r.data.requests : []))
        .catch(() => {});
        
      await fetchMyDeposits();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Failed to update payment status.');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleRequestWithdrawal = async () => {
    const amount = Math.trunc(Number(withdrawAmount) || 0);
    const isBank = withdrawProvider === 'bank_transfer';
    if (amount < minimumWithdrawal) {
      setWithdrawMessage(`Minimum withdrawal is ${minimumWithdrawal} wikipenny.`);
      return;
    }
    if (amount > Number(user?.balance || 0)) {
      setWithdrawMessage('Insufficient balance.');
      return;
    }

    if (isBank) {
      if (!String(withdrawBankName).trim()) {
        setWithdrawMessage('Please enter your Bank Name.');
        return;
      }
      if (!String(withdrawAccountNumber).trim() || String(withdrawAccountNumber).trim().length < 5) {
        setWithdrawMessage('Please enter a valid Bank Account Number.');
        return;
      }
      if (!String(withdrawAccountName).trim()) {
        setWithdrawMessage('Please enter the Bank Account Name.');
        return;
      }
    } else {
      if (!String(withdrawAddress || '').trim()) {
        setWithdrawMessage('Please enter your USDT wallet address.');
        return;
      }
    }

    setWithdrawLoading(true);
    setWithdrawMessage('Submitting withdrawal request...');
    try {
      const res = await paymentAPI.requestWithdrawal({
        amount,
        walletAddress: isBank ? String(withdrawAccountNumber).trim() : String(withdrawAddress).trim(),
        network: isBank ? String(withdrawBankName).trim() : String(withdrawNetwork || 'tron').trim().toLowerCase(),
        provider: withdrawProvider,
        bankName: isBank ? String(withdrawBankName).trim() : undefined,
        accountNumber: isBank ? String(withdrawAccountNumber).trim() : undefined,
        accountName: isBank ? String(withdrawAccountName).trim() : undefined,
      });

      const status = res?.data?.withdrawal?.status || 'pending_approval';
      const providerActionMessage = String(res?.data?.providerActionMessage || '').trim();
      const providerActionRequired = Boolean(res?.data?.providerActionRequired);
      if (providerActionRequired && providerActionMessage) {
        setWithdrawMessage(`⚠️ ${providerActionMessage}`);
      } else {
        setWithdrawMessage(`✅ Withdrawal submitted successfully! Status: ${status}`);
      }
      setWithdrawAmount('');
      setWithdrawAddress('');
      setWithdrawBankName('');
      setWithdrawAccountNumber('');
      setWithdrawAccountName('');
      await Promise.all([
        refreshUser?.(),
        fetchLedger({ silent: true }),
        fetchMyWithdrawals(),
        fetchAdminWithdrawalData()
      ]);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to submit withdrawal request');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const handleSetWithdrawalMode = async (mode) => {
    if (!isAdmin) return;
    setModeLoading(true);
    try {
      const res = await adminAPI.updateWithdrawalMode(mode);
      setWithdrawalMode(res?.data?.withdrawalMode || mode);
      setWithdrawMessage(`✅ Withdrawal mode updated to ${mode}`);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to update withdrawal mode');
    } finally {
      setModeLoading(false);
    }
  };

  const handleApprovePending = async (entry) => {
    const withdrawalId = entry?.id;
    const provider = String(entry?.provider || '').toLowerCase();
    const verificationCode = provider === 'nowpayments'
      ? window.prompt('Enter NOWPayments 2FA verification code to approve this payout:')
      : '';

    if (provider === 'nowpayments' && !String(verificationCode || '').trim()) {
      setWithdrawMessage('NOWPayments approval requires a 2FA verification code.');
      return;
    }

    setPendingActionId(withdrawalId);
    try {
      const res = await paymentAPI.approveWithdrawal(withdrawalId, verificationCode);
      const payoutError = String(res?.data?.payoutError || '').trim();
      if (payoutError) {
        setWithdrawMessage(`⚠️ ${payoutError}`);
      } else {
        setWithdrawMessage('✅ Withdrawal approved successfully!');
      }
      await Promise.all([fetchMyWithdrawals(), fetchAdminWithdrawalData(), fetchLedger({ silent: true }), refreshUser?.()]);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to approve withdrawal');
    } finally {
      setPendingActionId(null);
    }
  };  const handleRejectPending = async (withdrawalId) => {
    const reason = window.prompt('Please enter the reason for rejection (required):');
    if (reason === null) return; // User clicked Cancel
    if (!String(reason).trim()) {
      alert('A reason is required to reject a withdrawal.');
      return;
    }

    setPendingActionId(withdrawalId);
    try {
      await paymentAPI.rejectWithdrawal(withdrawalId, String(reason).trim());
      setWithdrawMessage('✅ Withdrawal rejected');
      await Promise.all([fetchMyWithdrawals(), fetchAdminWithdrawalData(), fetchLedger({ silent: true }), refreshUser?.()]);
    } catch (e) {
      setWithdrawMessage(e.response?.data?.error || e.message || 'Failed to reject withdrawal');
    } finally {
      setPendingActionId(null);
    }
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        {/* Header */}
        <div className="transactions-header">
          <div className="header-left">
            <div className="header-icon">💰</div>
            <div>
              <h1 className="page-title">Transactions</h1>
              <p className="page-subtitle">Manage your deposits and withdrawals</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            ← Dashboard
          </button>
        </div>

        {/* Balance Card */}
        <div className="balance-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="balance-info">
              <div className="balance-label">Available Balance</div>
              <div className="balance-value">{Number(user?.balance || 0).toLocaleString()} WKP</div>
            </div>
            <div className="balance-info" style={{ borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: 24 }}>
              <div className="balance-label" style={{ color: '#10b981' }}>🎁 Unlocked Bonus</div>
              <div className="balance-value" style={{ color: '#10b981' }}>{Number(user?.bonus_balance || 0).toLocaleString()} WKP</div>
            </div>
          </div>
          <div className="balance-actions">
            <button 
              className={`balance-action deposit ${activeTab === 'deposit' ? 'active' : ''}`}
              onClick={() => setActiveTab('deposit')}
            >
              💰 Deposit
            </button>
            <button 
              className={`balance-action withdraw ${activeTab === 'withdraw' ? 'active' : ''}`}
              onClick={() => setActiveTab('withdraw')}
            >
              💸 Withdraw
            </button>
          </div>
        </div>

        {/* Deposit Section */}
        {activeTab === 'deposit' && (
          <div className="transaction-card deposit-card">
            <div className="card-header">
              <h2 className="card-title">Deposit Funds</h2>
              <p className="card-subtitle">Choose your payment method</p>
            </div>

            <div className="provider-selector">
              {providerOptions.map((provider) => (
                <div
                  key={provider.value}
                  className={`provider-option ${depositProvider === provider.value ? 'selected' : ''}`}
                  onClick={() => setDepositProvider(provider.value)}
                >
                  <div className="provider-icon">{provider.icon}</div>
                  <div className="provider-info">
                    <div className="provider-name">{provider.label}</div>
                    <div className="provider-desc">{provider.description}</div>
                  </div>
                  {depositProvider === provider.value && (
                    <div className="provider-check">✓</div>
                  )}
                </div>
              ))}
            </div>

            {depositProvider === 'nowpayments' && (
              <>
                <div className="deposit-form">
                  <div className="form-group">
                    <label className="form-label">Amount ({depositCurrencyLabel})</label>
                    <div className="amount-input-wrapper">
                      <input
                        type="number"
                        className="amount-input"
                        min={Math.max(1, minimumDeposit)}
                        step="1"
                        value={depositAmount}
                        onChange={(e) => setDepositAmount(e.target.value)}
                      />
                      <div className="amount-suggestions">
                        <button className="suggestion" onClick={() => setDepositAmount('10')}>$10</button>
                        <button className="suggestion" onClick={() => setDepositAmount('25')}>$25</button>
                        <button className="suggestion" onClick={() => setDepositAmount('50')}>$50</button>
                        <button className="suggestion" onClick={() => setDepositAmount('100')}>$100</button>
                      </div>
                    </div>

                    <div className="deposit-amount-panel">
                      <div className="deposit-amount-row">
                        <span className="deposit-amount-label">If you send </span>
                        <strong className="deposit-amount-value"> {parsedDepositAmount} {depositCurrencyLabel}</strong>
                      </div>
                      <div className="deposit-amount-row">
                        <span className="deposit-amount-label">You will receive</span>
                        <strong className="deposit-amount-credit"> {estimatedDepositCredit.toLocaleString()} wkp</strong>
                      </div>
                      <div className="deposit-amount-rate">Rate: 1 USD/USDT = 100 wkp</div>
                    </div>

                    <div className="form-hint">
                      Minimum: {minimumDeposit} {depositCurrencyLabel}
                    </div>
                  </div>

                  <button 
                    className="deposit-btn"
                    onClick={handleStartDeposit} 
                    disabled={depositLoading}
                  >
                    {depositLoading ? (
                      <>
                        <span className="spinner"></span>
                        Processing...
                      </>
                    ) : (
                      `Deposit with ${selectedProviderLabel}`
                    )}
                  </button>
                  <div className="form-hint">
                    ⚠️ Send USDT on Binance Chain (BEP-20) only, and send the exact amount shown to avoid coin loss or processing delay.
                  </div>

                  {depositMessage && (
                    <div className={`message ${depositMessage.includes('✅') ? 'success' : depositMessage.includes('❌') ? 'error' : 'info'}`}>
                      {depositMessage}
                    </div>
                  )}

                  {pendingReference && (
                    <button
                      className="recheck-btn"
                      onClick={() => checkDepositStatus(pendingReference, { showLoading: true })}
                      disabled={depositLoading}
                    >
                      ⟳ Recheck Deposit Status
                    </button>
                  )}
                </div>

                {/* Deposit History */}
                <div className="deposit-history">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <h3 className="history-title" style={{ marginBottom: 0 }}>Deposit History</h3>
                    <select
                      className="form-select"
                      value={depositStatusFilter}
                      onChange={(e) => setDepositStatusFilter(String(e.target.value || '').trim())}
                      style={{ maxWidth: 220 }}
                    >
                      <option value="">All statuses</option>
                      <option value="pending">Pending</option>
                      <option value="success">Success</option>
                      <option value="partial_success">Partial Success</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  {depositsLoading ? (
                    <div className="loading-spinner">Loading...</div>
                  ) : deposits.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">💰</div>
                      <div className="empty-text">No deposits yet</div>
                    </div>
                  ) : (
                    <div className="history-list">
                      {deposits.map((row) => (
                        <div key={row.id} className="history-item">
                          <div className="history-info">
                            <div className="history-amount">{row.amount} {row.currency || 'USD'}</div>
                            <div className="history-date">{formatDate(row.createdAt || row.created_at)}</div>
                          </div>
                          <div className="history-details">
                            <span className={`history-status status-${row.status}`}>{formatDepositStatus(row.status)}</span>
                            <span className="history-network">{row.provider || '—'}</span>
                            <button
                              type="button"
                              className="suggestion"
                              style={{ padding: '4px 10px', marginTop: '6px', fontSize: '12px' }}
                              onClick={() => handleViewDeposit(row)}
                            >
                              View
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            {depositProvider === 'bank_transfer' && (
              <>
                {/* Step 1 – Enter amount */}
                <div className="deposit-form">
                  <div className="form-group">
                    <label className="form-label">Amount to Transfer (₦ Naira)</label>
                    <div className="amount-input-wrapper">
                      <input
                        type="number"
                        className="amount-input"
                        min="100"
                        step="100"
                        value={bankDepositAmount}
                        onChange={(e) => { setBankDepositAmount(e.target.value); setBankTransferRequest(null); setBankTransferMessage(''); }}
                        placeholder="e.g. 1500"
                      />
                      <div className="amount-suggestions">
                        {[1500, 3000, 7500, 15000].map((p) => (
                          <button key={p} className="suggestion" onClick={() => { setBankDepositAmount(String(p)); setBankTransferRequest(null); setBankTransferMessage(''); }}>₦{p.toLocaleString()}</button>
                        ))}
                      </div>
                    </div>

                    {/* Conversion preview */}
                    {(() => {
                      const naira = Math.round(Number(bankDepositAmount) * 100) / 100 || 0;
                      const rate = bankSettings?.nairaDollarRate || 1500;
                      const wkp = naira > 0 ? Math.max(1, Math.round((naira / rate) * 100)) : 0;
                      return naira > 0 ? (
                        <div className="deposit-amount-panel">
                          <div className="deposit-amount-row">
                            <span className="deposit-amount-label">You transfer</span>
                            <strong className="deposit-amount-value"> ₦{naira.toLocaleString()}</strong>
                          </div>
                          <div className="deposit-amount-row">
                            <span className="deposit-amount-label">You receive</span>
                            <strong className="deposit-amount-credit"> {wkp.toLocaleString()} WKP</strong>
                          </div>
                          <div className="deposit-amount-rate">Rate: 100 WKP = ₦{rate.toLocaleString()}</div>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">Payment Description / Sender Account Name</label>
                    <input
                      type="text"
                      className="amount-input"
                      value={bankDepositDescription}
                      onChange={(e) => { setBankDepositDescription(e.target.value); setBankTransferRequest(null); setBankTransferMessage(''); }}
                      placeholder="e.g. John Doe - GTBank"
                    />
                    <small style={{ color: '#9ca3af', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                      Enter the name on the bank account you are transferring from, or your narration details, to help us verify your deposit.
                    </small>
                  </div>

                  <button
                    className="deposit-btn"
                    onClick={handleBankTransferRequest}
                    disabled={bankTransferLoading || !Number(bankDepositAmount) || Number(bankDepositAmount) < 100 || !bankDepositDescription || bankDepositDescription.trim().length < 3}
                  >
                    {bankTransferLoading ? <><span className="spinner" /> Generating...</> : '🔑 Generate My Payment Code'}
                  </button>

                  {bankTransferMessage && (
                    <div className={`message ${bankTransferMessage.includes('✅') ? 'success' : 'error'}`}>
                      {bankTransferMessage}
                    </div>
                  )}
                </div>

                {/* Step 2 – Show reference code + bank details after request created */}
                {bankTransferRequest && (
                  <div style={{
                    margin: '24px 0',
                    padding: '24px',
                    background: 'rgba(16,185,129,0.08)',
                    border: '1.5px solid rgba(16,185,129,0.35)',
                    borderRadius: 16,
                  }}>
                    <h3 style={{ margin: '0 0 16px', color: '#10b981', fontSize: 17 }}>📋 Your Payment Instructions</h3>

                    {/* Reference code — prominent */}
                    <div style={{
                      background: 'rgba(16,185,129,0.15)',
                      border: '2px dashed #10b981',
                      borderRadius: 12,
                      padding: '16px 20px',
                      marginBottom: 20,
                      textAlign: 'center',
                    }}>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6, letterSpacing: 1 }}>YOUR UNIQUE PAYMENT CODE</div>
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#10b981', letterSpacing: 4, fontFamily: 'monospace' }}>
                        {bankTransferRequest.reference}
                      </div>
                      <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 8, fontWeight: 600 }}>
                        ⚠️ You MUST include this code in your transfer narration/description
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#9ca3af', marginBottom: 10, letterSpacing: 0.5 }}>BANK ACCOUNT DETAILS</div>
                      {[['Bank', bankSettings?.bankName || '—'], ['Account Number', bankSettings?.accountNumber || bankSettings?.bankAccountNumber || '—'], ['Account Name', bankSettings?.accountName || bankSettings?.bankAccountName || '—']].map(([k, v]) => (
                        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <span style={{ color: '#9ca3af', fontSize: 13 }}>{k}</span>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{v}</span>
                        </div>
                      ))}
                    </div>

                    {/* Amount summary */}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 120, background: 'rgba(99,102,241,0.1)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>Transfer Amount</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#6366f1' }}>₦{Number(bankTransferRequest.amountNaira).toLocaleString()}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 120, background: 'rgba(16,185,129,0.1)', borderRadius: 10, padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>You Will Receive</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>{Number(bankTransferRequest.amountWkp).toLocaleString()} WKP</div>
                      </div>
                    </div>

                    {/* Instructions */}
                    <ol style={{ margin: 0, paddingLeft: 20, color: '#d1d5db', fontSize: 14, lineHeight: 1.8 }}>
                      <li>Open your mobile banking or USSD app</li>
                      <li>Transfer <strong style={{ color: '#fff' }}>₦{Number(bankTransferRequest.amountNaira).toLocaleString()}</strong> to the account above</li>
                      <li>In the <strong style={{ color: '#f59e0b' }}>narration/description</strong> field, type: <strong style={{ color: '#10b981', fontFamily: 'monospace', fontSize: 16 }}>{bankTransferRequest.reference}</strong></li>
                      <li>Your wallet will be credited within <strong style={{ color: '#fff' }}>~20 minutes</strong></li>
                    </ol>

                    <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(245,158,11,0.1)', borderRadius: 10, fontSize: 13, color: '#fbbf24' }}>
                      ⏰ Approval time: ~20 minutes · Code expires in 24 hours
                    </div>

                    {/* Payment Confirmation Button */}
                    {bankTransferRequest.status === 'paid' ? (
                      <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 10, fontSize: 14, color: '#60a5fa', fontWeight: 'bold', textAlign: 'center' }}>
                        ✔️ Marked as Paid! Awaiting Admin verification.
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="deposit-btn"
                        style={{ marginTop: 16, background: '#10b981', color: '#000', fontWeight: 'bold', width: '100%' }}
                        disabled={markingPaid}
                        onClick={() => handleConfirmPaymentMade(bankTransferRequest.id, bankTransferRequest.reference)}
                      >
                        {markingPaid ? 'Updating status...' : '✔️ I Have Made This Payment'}
                      </button>
                    )}

                    {/* WhatsApp contact */}
                    <div style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(37,211,102,0.1)', border: '1px solid rgba(37,211,102,0.3)', borderRadius: 10, fontSize: 13 }}>
                      <span style={{ color: '#25d366', fontWeight: 700 }}>📱 Delayed beyond 20 minutes?</span>
                      <span style={{ color: '#d1d5db' }}> Contact us on WhatsApp: </span>
                      <a
                        href={`https://wa.me/2347078030610?text=Hi%2C%20I%20made%20a%20bank%20transfer%20with%20code%20${bankTransferRequest.reference}%20and%20my%20wallet%20has%20not%20been%20credited.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#25d366', fontWeight: 800, textDecoration: 'none' }}
                      >
                        +234 707 803 0610
                      </a>
                    </div>
                  </div>
                )}

                {/* Previous bank transfer requests */}
                {myBankTransfers.length > 0 && (
                  <div className="deposit-history">
                    <h3 className="history-title">My Bank Transfer Requests</h3>
                    {myBankTransfersLoading ? (
                      <div className="loading-spinner">Loading...</div>
                    ) : (
                      <div className="history-list">
                        {myBankTransfers.map((r) => (
                          <div key={r.id} className="history-item">
                            <div className="history-info">
                              <div className="history-amount" style={{ fontFamily: 'monospace', fontWeight: 800, color: '#10b981' }}>{r.reference}</div>
                              <div className="history-date">₦{Number(r.amountNaira).toLocaleString()} → {Number(r.amountWkp).toLocaleString()} WKP</div>
                              {r.description && <div style={{ fontSize: '11px', color: '#fbbf24', marginTop: '2px', wordBreak: 'break-all' }}>Desc: "{r.description}"</div>}
                              <div className="history-date">{formatDate(r.createdAt)}</div>
                            </div>
                            <div className="history-details">
                              <span className={`history-status status-${r.status}`}>
                                {r.status === 'approved' ? '✅ Approved' : r.status === 'rejected' ? '❌ Rejected' : r.status === 'paid' ? '✔️ Paid (Awaiting Verification)' : '⏳ Pending'}
                              </span>
                              {r.adminNote && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{r.adminNote}</div>}
                              {r.status === 'pending' && (
                                <button
                                  type="button"
                                  className="suggestion"
                                  style={{ padding: '4px 10px', marginTop: '6px', fontSize: '12px', background: '#10b981', color: '#000', fontWeight: 'bold', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  onClick={() => handleConfirmPaymentMade(r.id, r.reference)}
                                >
                                  ✔️ Mark as Paid
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {(selectedDeposit || depositReceiptLoading) && (
          <div
            onClick={() => {
              if (depositReceiptLoading) return;
              setSelectedDeposit(null);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.55)',
              zIndex: 4000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(560px, 96vw)',
                borderRadius: '16px',
                background: '#111827',
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
                padding: '16px',
                color: '#e5e7eb',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3 style={{ margin: 0 }}>Deposit Receipt</h3>
                <button type="button" className="suggestion" onClick={() => setSelectedDeposit(null)} disabled={depositReceiptLoading}>Close</button>
              </div>

              {depositReceiptLoading && !selectedDeposit ? (
                <div className="loading-spinner">Loading receipt...</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px 12px', fontSize: '14px' }}>
                  <strong>Reference</strong><span style={{ wordBreak: 'break-all' }}>{selectedDeposit?.reference || '—'}</span>
                  <strong>Provider</strong><span>{selectedDeposit?.provider || '—'}</span>
                  <strong>Status</strong><span>{formatDepositStatus(selectedDeposit?.status)}</span>
                  <strong>Provider Status</strong><span>{selectedDeposit?.providerStatus || selectedDeposit?.receipt?.providerStatus || '—'}</span>
                  <strong>Requested</strong><span>{formatMoney(selectedDeposit?.requestedAmount ?? selectedDeposit?.amount, selectedDeposit?.currency || 'USD')}</span>
                  <strong>Received</strong><span>{formatMoney(selectedDeposit?.receivedAmount, selectedDeposit?.currency || 'USD')}</span>
                  <strong>Credited</strong><span>{formatMoney(selectedDeposit?.creditedUsdAmount ?? selectedDeposit?.creditedAmount, selectedDeposit?.currency || 'USD')}</span>
                  <strong>WikiPenny Credited</strong><span>{Number(selectedDeposit?.creditedWikiPenny || selectedDeposit?.receipt?.creditedWikiPenny || 0).toLocaleString()} WKP</span>
                  <strong>Transaction ID</strong><span style={{ wordBreak: 'break-all' }}>{selectedDeposit?.providerTransactionId || selectedDeposit?.receipt?.providerTransactionId || '—'}</span>
                  <strong>Invoice ID</strong><span>{selectedDeposit?.receipt?.invoiceId || '—'}</span>
                  <strong>Payment ID</strong><span>{selectedDeposit?.receipt?.paymentId || '—'}</span>
                  <strong>Pay Currency</strong><span>{selectedDeposit?.receipt?.payCurrency || '—'}</span>
                  <strong>Pay Amount</strong><span>{formatMoney(selectedDeposit?.receipt?.payAmount, selectedDeposit?.receipt?.payCurrency || '')}</span>
                  <strong>Actually Paid</strong><span>{selectedDeposit?.receipt?.actuallyPaid || '—'}</span>
                  <strong>Created At</strong><span>{formatDate(selectedDeposit?.createdAt || selectedDeposit?.created_at)}</span>
                  <strong>Paid At</strong><span>{formatDate(selectedDeposit?.paidAt || selectedDeposit?.paid_at)}</span>
                  <strong>Credited At</strong><span>{formatDate(selectedDeposit?.creditedAt || selectedDeposit?.credited_at)}</span>
                  <strong>Updated At</strong><span>{formatDate(selectedDeposit?.updatedAt || selectedDeposit?.updated_at)}</span>
                  <strong>Deposit ID</strong><span>{selectedDeposit?.id ?? '—'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Withdrawal Section */}
        {activeTab === 'withdraw' && (
          <div className="transaction-card withdraw-card">
            <div className="card-header">
              <h2 className="card-title">Withdraw Funds</h2>
              <p className="card-subtitle">Choose your withdrawal method and enter details</p>
            </div>

            <div className="withdraw-form">
              <div className="form-group">
                <label className="form-label">Withdrawal Method</label>
                <select 
                  className="form-select"
                  value={withdrawProvider} 
                  onChange={(e) => setWithdrawProvider(e.target.value)}
                >
                  <option value="nowpayments">USDT Crypto (NOWPayments)</option>
                  <option value="bank_transfer">Naira Bank Transfer (Manual)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Amount (WikiPenny WKP)</label>
                <input
                  type="number"
                  className="form-input"
                  min={Math.max(0, minimumWithdrawal)}
                  step="1"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  placeholder="Enter amount in wikipenny"
                />
                
                {withdrawProvider === 'nowpayments' ? (
                  <div className="deposit-amount-panel">
                    <div className="deposit-amount-row">
                      <span className="deposit-amount-label">You withdraw</span>
                      <strong className="deposit-amount-value">{Math.max(0, Math.trunc(Number(withdrawAmount) || 0)).toLocaleString()} WKP</strong>
                    </div>
                    <div className="deposit-amount-row">
                      <span className="deposit-amount-label">You receive</span>
                      <strong className="deposit-amount-credit">~{(Math.max(0, Math.trunc(Number(withdrawAmount) || 0)) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT</strong>
                    </div>
                    <div className="deposit-amount-rate">Rate: 100 WKP = 1 USDT</div>
                  </div>
                ) : (
                  <div className="deposit-amount-panel">
                    <div className="deposit-amount-row">
                      <span className="deposit-amount-label">You withdraw</span>
                      <strong className="deposit-amount-value">{Math.max(0, Math.trunc(Number(withdrawAmount) || 0)).toLocaleString()} WKP</strong>
                    </div>
                    {(() => {
                      const amount = Math.trunc(Number(withdrawAmount) || 0);
                      const rate = bankSettings?.nairaDollarRate || 1500;
                      const naira = Math.trunc((amount / 100) * rate);
                      return (
                        <>
                          <div className="deposit-amount-row">
                            <span className="deposit-amount-label">You receive</span>
                            <strong className="deposit-amount-credit">~₦{naira.toLocaleString()} Naira</strong>
                          </div>
                          <div className="deposit-amount-rate">Rate: 100 WKP = ₦{rate.toLocaleString()}</div>
                        </>
                      );
                    })()}
                  </div>
                )}

                <div className="form-hint">
                  Minimum: {minimumWithdrawal} WKP. Balance: {Number(user?.balance || 0).toLocaleString()} WKP
                </div>
              </div>

              {withdrawProvider === 'nowpayments' ? (
                <>
                  <div className="form-group">
                    <label className="form-label">USDT Wallet Address</label>
                    <input
                      type="text"
                      className="form-input"
                      value={withdrawAddress}
                      onChange={(e) => setWithdrawAddress(e.target.value)}
                      placeholder="Enter USDT wallet address"
                    />
                    <div className="form-hint">Make sure to use the correct network (BSC BEP-20 recommended)</div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Network</label>
                    <div className="network-selector">
                      <button 
                        className={`network-option ${withdrawNetwork === 'bsc' ? 'selected' : ''}`}
                        onClick={() => setWithdrawNetwork('bsc')}
                      >
                        <span className="network-icon">🔷</span>
                        <span className="network-name">BEP-20 (BSC)</span>
                        <span className="network-fee">~$0.10 fee</span>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={withdrawBankName}
                      onChange={(e) => setWithdrawBankName(e.target.value)}
                      placeholder="e.g. Access Bank, GTBank, OPay"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bank Account Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={withdrawAccountNumber}
                      onChange={(e) => setWithdrawAccountNumber(e.target.value)}
                      placeholder="e.g. 0123456789"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Account Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={withdrawAccountName}
                      onChange={(e) => setWithdrawAccountName(e.target.value)}
                      placeholder="Enter full bank account name"
                    />
                  </div>
                </>
              )}

              <button 
                className="withdraw-btn"
                onClick={handleRequestWithdrawal} 
                disabled={withdrawLoading}
              >
                {withdrawLoading ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  'Request Withdrawal'
                )}
              </button>

              {withdrawMessage && (
                <div className={`message ${withdrawMessage.includes('✅') ? 'success' : withdrawMessage.includes('❌') ? 'error' : 'info'}`}>
                  {withdrawMessage}
                </div>
              )}
            </div>

            {/* Withdrawal History */}
            <div className="withdrawal-history">
              <h3 className="history-title">Withdrawal History</h3>
              {withdrawalsLoading ? (
                <div className="loading-spinner">Loading...</div>
              ) : withdrawals.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">💸</div>
                  <div className="empty-text">No withdrawal requests yet</div>
                </div>
              ) : (
                <div className="history-list">
                  {withdrawals.map((row) => (
                    <div key={row.id} className="history-item">
                      <div className="history-info">
                        <div className="history-amount">{row.amount} {row.currency || 'USDT'}</div>
                        <div className="history-date">{formatDate(row.created_at)}</div>
                      </div>
                      <div className="history-details">
                        <span className={`history-status status-${row.status}`}>{row.status || '—'}</span>
                        <span className="history-network">{row.network || 'TRC-20'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Admin Section */}
        {isAdmin && (
          <div className="admin-section">
            <div className="admin-card">
              <div className="card-header">
                <h2 className="card-title">Admin Controls</h2>
                <p className="card-subtitle">Withdrawal mode management</p>
              </div>

              <div className="mode-controls">
                <div className="mode-info">
                  <span className="mode-label">Current Mode:</span>
                  <span className={`mode-value ${withdrawalMode}`}>
                    {withdrawalMode === 'admin_approve' ? '🔒 Admin Approval Required' : '⚡ Auto-Approval'}
                  </span>
                </div>
                <div className="mode-actions">
                  <button
                    className={`mode-btn ${withdrawalMode === 'admin_approve' ? 'active' : ''}`}
                    onClick={() => handleSetWithdrawalMode('admin_approve')}
                    disabled={modeLoading}
                  >
                    Admin Approve
                  </button>
                  <button
                    className={`mode-btn ${withdrawalMode === 'auto' ? 'active' : ''}`}
                    onClick={() => handleSetWithdrawalMode('auto')}
                    disabled={modeLoading}
                  >
                    Auto
                  </button>
                </div>
              </div>

              {pendingLoading ? (
                <div className="loading-spinner">Loading pending withdrawals...</div>
              ) : pendingWithdrawals.length > 0 && (
                <div className="pending-withdrawals">
                  <h3 className="pending-title">Pending Withdrawals ({pendingWithdrawals.length})</h3>
                  <div className="pending-list">
                    {pendingWithdrawals.map((row) => (
                      <div key={row.id} className="pending-item">
                        <div className="pending-info">
                          <div className="pending-user">{row.username || row.user_id}</div>
                          <div className="pending-amount">{row.amount} {row.currency || 'USDT'}</div>
                          <div className="pending-address">{row.wallet_address?.slice(0, 20)}...</div>
                        </div>
                        <div className="pending-actions">
                          <button
                            className="approve-btn"
                            disabled={pendingActionId === row.id}
                            onClick={() => handleApprovePending(row)}
                          >
                            Approve
                          </button>
                          <button
                            className="reject-btn"
                            disabled={pendingActionId === row.id}
                            onClick={() => handleRejectPending(row.id)}
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-header">
            <h3 className="filters-title">Transaction History</h3>
            <div className="filters-controls">
              <select 
                className="filter-select"
                value={categoryFilter} 
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="deposit">Deposits</option>
                <option value="withdrawal">Withdrawals</option>
                <option value="wager">Wagers</option>
                <option value="payout">Payouts</option>
                <option value="refund">Refunds</option>
              </select>
              <select 
                className="filter-select"
                value={gameFilter} 
                onChange={(e) => setGameFilter(e.target.value)}
              >
                <option value="">All Games</option>
                <option value="whot">WHOT</option>
                <option value="chess">Chess</option>
                <option value="dice">Dice</option>
                <option value="flappy">Flappy</option>
                <option value="reaction">Reaction</option>
              </select>
            </div>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading transactions...</span>
            </div>
          )}

          {!loading && error && (
            <div className="error-state">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && (
            filteredEntries.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <div className="empty-text">No transactions found</div>
              </div>
            ) : (
              <>
              <div className="transactions-list">
                {paginatedEntries.map((entry) => (
                  <div key={entry.id} className="transaction-item">
                    <div className="transaction-icon">{getTransactionIcon(entry)}</div>
                    <div className="transaction-info">
                      <div className="transaction-category">
                        {formatCategoryLabel(entry.category)}
                        {(entry.resolved_game || entry.game) && <span className="transaction-game"> • {toDisplayGameName(entry.resolved_game || entry.game)}</span>}
                      </div>
                      <div className="transaction-desc">{formatDetailsLabel(entry)}</div>
                      <div className="transaction-date">{formatDate(entry.created_at)}</div>
                    </div>
                    <div className="transaction-amount" style={{ color: getAmountColor(entry.delta) }}>
                      {formatDelta(entry.delta)}
                    </div>
                    <div className="transaction-balance">{entry.balance_after}</div>
                  </div>
                ))}
              </div>

              <div className="transactions-pagination">
                <button
                  className="pagination-btn"
                  disabled={safeTransactionsPage <= 1}
                  onClick={() => setTransactionsPage((prev) => Math.max(1, prev - 1))}
                >
                  ‹ Prev
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: totalTransactionPages }).slice(0, 7).map((_, idx) => {
                    const pageNumber = idx + 1;
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-page ${safeTransactionsPage === pageNumber ? 'active' : ''}`}
                        onClick={() => setTransactionsPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="pagination-btn"
                  disabled={safeTransactionsPage >= totalTransactionPages}
                  onClick={() => setTransactionsPage((prev) => Math.min(totalTransactionPages, prev + 1))}
                >
                  Next ›
                </button>
              </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Ledger;

